import { LinkCreationService, defaultHandleTeamClick, getTeamColor } from '@sportal365/smp-widget-utils';
import LineupCollapsible from './LineupCollapsible';
import { defaultHandleUserInteractionClick } from '@sportal365/smp-widget-utils/src/clickHandlers';
import { Property } from '@stitches/react/types/css';
import { Field, SMPLinkWrapper, SMPText, ConfigContext } from '@velinsi/smp-widgets-ui';
import { useEffect, useMemo, useState } from 'react';
import { TEAM_ENTITY, USER_INTERACTION } from '../../../../../packages/constants/entityTypes';
import { GenericAlign, GenericLayout, MAX_WIDGET_WIDTH } from '../../../../../packages/constants/generic';
import { NATIONAL } from '../../../../../packages/constants/sportRegions';
import { TAB_SELECTION_SUBSCRIBE_TYPE } from '../../../../../packages/constants/subscribeTypes';
import { TARGET_BLANK, TARGET_SELF } from '../../../../../packages/constants/windowTarget';
import useResizeObserver from '../../../../../packages/hooks/useResizeObserver';
import useRootNodeRef from '../../../../../packages/hooks/useRootNodeRef';
import ThemeProvider from '../../../../../packages/theme/ThemeProvider';
import { styled } from '../../../../../packages/theme/stitches.config';
import { BaseWidgetProps, EventTypeCode, IFootballLineupsConfig, IMatchPlayerLineup, INormalizedTeam } from '../../../../../packages/types';
import LinkServiceContext from '../../contexts/LinkServiceContext';
import useLineups from '../../hooks/useLineups';
import useSingleEvent from '../../hooks/useSingleEvent';
import { THUMB_SIZE_BREAKPOINT_3 } from './LineupsPlayer';
import { CoachContainer } from './CoachContainer';
import { LineupsProfileHead } from './LineupsProfileHead';
import { TeamFormationDisplay } from './TeamFormationDisplay';

// Note: the following aspect ratios are based on the sizes of the field background images (in file Field.tsx)

const ALIGNMENT_BREAKPOINT_1 = 772; // Width below which the widget switches to vertical mode
const ALIGNMENT_BREAKPOINT_2 = 393; // Width below which the widget switches to vertical-condensed mode

enum LineupsDetails {
  COACH = 'coach',
  FIRST_TEAM = 'first_team',
  SUBSTITUTES = 'substitutes',
}

type DetailsVisibility = Record<LineupsDetails, { show: boolean; status?: 'open' | 'closed' }> & { showTabs: boolean };

interface LineupsInfo {
  first: IMatchPlayerLineup[];
  substitutions: Partial<IMatchPlayerLineup>[];
  substituedPlayers: Partial<IMatchPlayerLineup>[];
  notSubstitutedPlayers: Partial<IMatchPlayerLineup>[];
  formation: Record<number, IMatchPlayerLineup[]>;
  hasSubstitution: boolean;
}

export type LineupsWidgetProps = BaseWidgetProps<IFootballLineupsConfig>;

type MobileTabOptions = 0 | 1;

export const LineupsWidget = ({ themes, config, subscribers, shadow, cuid }: LineupsWidgetProps) => {
  const [fieldWidth, setFieldWidth] = useState(MAX_WIDGET_WIDTH);
  const [fieldWrapperWidth, setFieldWrapperWidth] = useState(MAX_WIDGET_WIDTH);
  const [lineColor, setLineColor] = useState<Property.Color>('unset');
  const [selectedTabMobile, setSelectedTabMobile] = useState<MobileTabOptions>(0);
  const { ref } = useRootNodeRef();

  const mode = useMemo(() => {
    // Layout changes depending on the width of the widget
    if (fieldWrapperWidth < ALIGNMENT_BREAKPOINT_2) {
      return GenericLayout.VERTICAL_CONDENSED;
    }

    if (fieldWrapperWidth < ALIGNMENT_BREAKPOINT_1) {
      return GenericLayout.VERTICAL;
    }

    return GenericLayout.HORIZONTAL;
  }, [fieldWrapperWidth]);
  const [fieldHTMLElement, setFieldHTMLElement] = useState<HTMLElement>();
  const measuredRef = useResizeObserver((fieldElement) => {
    setFieldHTMLElement(fieldElement);
    setFieldWidth(fieldElement.offsetWidth);
  });
  const fieldWrapperRef = useResizeObserver((wrapperElement) => {
    setFieldWrapperWidth(wrapperElement.offsetWidth);
  });

  const {
    showWidget: showWidgetA,
    widgetData: { lineups, isLoading: isLoadingLineups },
    handleClick,
  } = useLineups({
    subscribers,
    dataWidgetId: config?.dataWidgetId,
    dataMatchId: config?.dataMatchId,
    dataWidgetSport: config?.dataWidgetSport,
    dataRefreshTime: config?.dataRefreshTime,
    dataWidgetCuid: cuid,
  });

  const {
    showWidget: showWidgetB,
    widgetData: { footballSingleEvent, isLoading: isLoadingMatch },
  } = useSingleEvent({
    subscribers,
    dataWidgetId: config?.dataWidgetId,
    dataMatchId: config?.dataMatchId,
    dataWidgetSport: config?.dataWidgetSport,
  });

  const handleTabClickAndSendInteractionEvent = (selectedTab: MobileTabOptions) => {
    setSelectedTabMobile(selectedTab);
    defaultHandleUserInteractionClick(handleClick, USER_INTERACTION, TAB_SELECTION_SUBSCRIBE_TYPE);
  };

  const activePlayers = useMemo(() => {
    // Processes the lineups data in order to display the active players only
    const result: {
      homeTeam: LineupsInfo;
      awayTeam: LineupsInfo;
    } = {
      homeTeam: {
        first: [],
        substitutions: [],
        substituedPlayers: [],
        notSubstitutedPlayers: [],
        formation: {},
        hasSubstitution: false,
      },
      awayTeam: {
        first: [],
        substitutions: [],
        substituedPlayers: [],
        notSubstitutedPlayers: [],
        formation: {},
        hasSubstitution: false,
      },
    };

    if (!lineups?.homeTeam?.teamId || !lineups.awayTeam?.teamId) {
      return result;
    }

    result.homeTeam.first = lineups.homeTeam.players.filter((playerLineup) => playerLineup?.type.category === 'start');
    result.homeTeam.substitutions = lineups.homeTeam.players.filter((playerLineup) => playerLineup?.type.category === 'sub');
    result.awayTeam.first = lineups.awayTeam.players.filter((playerLineup) => playerLineup?.type.category === 'start');
    result.awayTeam.substitutions = lineups.awayTeam.players.filter((playerLineup) => playerLineup?.type.category === 'sub');

    // Split substitutions into substituedPlayers and notSubstitutedPlayers
    (['homeTeam', 'awayTeam'] as (keyof typeof result)[]).forEach((team) => {
      // Mark players with substitution events
      result[team].substituedPlayers = result[team].substitutions.filter((player) =>
        player.events?.some((event) => event.typeCode === EventTypeCode.SUBSTITUTION),
      );

      result[team].notSubstitutedPlayers = result[team].substitutions.filter(
        (player) => !player.events?.some((event) => event.typeCode === EventTypeCode.SUBSTITUTION),
      );

      // Set hasSubstitution flag
      result[team].hasSubstitution = result[team].substituedPlayers.length > 0;

      // Process formation data
      result[team].first.forEach((player) => {
        const positionX = player.positionX as number;

        if (positionX === null) {
          return;
        }

        if (!result[team].formation[positionX]) {
          result[team].formation[positionX] = [player];
        } else {
          result[team].formation[positionX].push(player);
        }
      });
    });

    return result;
  }, [lineups]);

  const hasPlayers = useMemo(() => {
    return activePlayers.homeTeam.first.length > 0 || activePlayers.awayTeam.first.length > 0;
  }, [activePlayers]);

  const hasFormation = useMemo(() => {
    return Object.keys(activePlayers.homeTeam.formation).length > 0 && Object.keys(activePlayers.awayTeam.formation).length > 0;
  }, [activePlayers]);

  const linkService = useMemo(() => config?.dataEntityLinks && new LinkCreationService(config.dataEntityLinks), [config?.dataEntityLinks]);
  const urlTarget = useMemo(
    () => (config?.dataEntityLinks?.configuration?.newWindow ? TARGET_BLANK : TARGET_SELF),
    [config?.dataEntityLinks],
  );

  const teamHeads = useMemo(() => {
    if (!config?.dataHeaderDisplay || !footballSingleEvent) {
      return null;
    }

    const { homeTeam, awayTeam } = footballSingleEvent;

    const handleTeamOneClick = () => defaultHandleTeamClick(handleClick, { id: homeTeam.id, name: homeTeam.name });
    const handleTeamTwoClick = () => defaultHandleTeamClick(handleClick, { id: awayTeam.id, name: awayTeam.name });

    const isNationalTeam = homeTeam.type === NATIONAL;

    return [
      <LineupsProfileHead
        key={homeTeam.id}
        team={{ logo: homeTeam.assets?.logo?.url ?? '', name: homeTeam.name, type: homeTeam.type } as INormalizedTeam}
        align={GenericAlign.LEFT}
        primaryLink={{
          url: linkService?.createEntityUrl(String(homeTeam.id), TEAM_ENTITY, '', homeTeam.slug) ?? '#',
          urlTarget,
          callback: handleTeamOneClick,
        }}
        isMainImageRound={isNationalTeam}
      />,
      <LineupsProfileHead
        key={awayTeam.id}
        team={{ logo: awayTeam.assets?.logo?.url ?? '', name: awayTeam.name, type: awayTeam.type } as INormalizedTeam}
        align={mode === GenericLayout.HORIZONTAL ? GenericAlign.RIGHT : GenericAlign.LEFT}
        primaryLink={{
          url: linkService?.createEntityUrl(String(awayTeam.id), TEAM_ENTITY, '', awayTeam.slug) ?? '#',
          urlTarget,
          callback: handleTeamTwoClick,
        }}
        isMainImageRound={isNationalTeam}
      />,
    ];
  }, [config?.dataHeaderDisplay, footballSingleEvent, handleClick, linkService, urlTarget, mode]);

  const details: DetailsVisibility = useMemo(() => {
    const firstTeamDefaultState =
      (activePlayers.homeTeam.hasSubstitution || activePlayers.awayTeam.hasSubstitution) && hasFormation ? 'closed' : 'open';

    if (!config?.dataElements) {
      return {
        [LineupsDetails.COACH]: { show: true },
        [LineupsDetails.FIRST_TEAM]: { show: true, status: firstTeamDefaultState },
        [LineupsDetails.SUBSTITUTES]: { show: true, status: 'closed' },
        showTabs: true,
      };
    }

    const result: DetailsVisibility = {
      [LineupsDetails.COACH]: { show: false },
      [LineupsDetails.FIRST_TEAM]: { show: false, status: 'closed' },
      [LineupsDetails.SUBSTITUTES]: { show: false, status: 'closed' },
      showTabs: false,
    };

    config.dataElements.forEach((element) => {
      const name = (typeof element === 'string' ? element : element.name) as LineupsDetails;

      if (![LineupsDetails.COACH, LineupsDetails.FIRST_TEAM, LineupsDetails.SUBSTITUTES].includes(name)) {
        return;
      }

      result.showTabs = true;
      result[name].show = true;

      if (name !== LineupsDetails.COACH) {
        result[name].status = typeof element === 'string' ? 'closed' : element.status;
      }
    });

    return result;
  }, [config?.dataElements, activePlayers, hasFormation]);

  useEffect(() => {
    if (!fieldHTMLElement) {
      return;
    }

    const computedStyle = getComputedStyle(fieldHTMLElement);
    setLineColor(computedStyle.getPropertyValue('--colors-footballFieldLineColor'));
  }, [config?.dataTheme, fieldHTMLElement]);

  return (
    <ConfigContext.Provider value={{ ...config }}>
      <ThemeProvider
        css={{
          backgroundColor: '$lineupsPrimaryBackgroundColor',
          overflow: 'hidden',
          '*': { boxSizing: 'border-box' },
          maxWidth: `${MAX_LINEUPS_WIDTH}px`,
        }}
        slickStyles={true}
        shadow={shadow}
        themes={themes}
        dataTheme={config?.dataTheme}
        isLoading={isLoadingLineups || isLoadingMatch}
        refForShadow={ref}
        removeMaxWidth
      >
        {!showWidgetA || !showWidgetB || !lineups?.homeTeam?.teamId || !lineups.awayTeam?.teamId || !footballSingleEvent || !hasPlayers ? (
          <SMPText textAlign='center' variant='contrast'>
            {config?.dataLabels?.no_data ?? 'No data to display'}
          </SMPText>
        ) : (
          <LinkServiceContext.Provider value={{ linkService: linkService, urlTarget: urlTarget }}>
            <FieldWrapper ref={fieldWrapperRef}>
              {config?.dataHeaderDisplay && teamHeads && (
                <>
                  {mode === GenericLayout.HORIZONTAL && (
                    <DesktopHeader
                      wide={
                        (details[LineupsDetails.COACH].show && lineups.homeTeam.coach !== null && lineups.awayTeam.coach !== null) ||
                        hasFormation
                      }
                    >
                      {teamHeads[0]}
                      {teamHeads[1]}
                    </DesktopHeader>
                  )}
                  {mode !== GenericLayout.HORIZONTAL && (
                    <MobileHeaderFooter small={fieldWidth < THUMB_SIZE_BREAKPOINT_3}>{teamHeads[0]}</MobileHeaderFooter>
                  )}
                </>
              )}
              {hasFormation && (
                <FieldContainer
                  ref={measuredRef}
                  mode={mode} // Pass mode directly
                  noHeader={mode === GenericLayout.HORIZONTAL && !config?.dataHeaderDisplay}
                >
                  <Field mode={mode} lineColor={lineColor} />
                  {/* Use the new TeamFormationDisplay component */}
                  <TeamFormationDisplay
                    teamType='home'
                    formationData={activePlayers.homeTeam.formation}
                    mode={mode}
                    fieldWidth={fieldWidth}
                    borderColor={getTeamColor(config?.dataHomeTeamColor, footballSingleEvent.homeTeam.shirtColor)}
                    handleClick={handleClick}
                  />
                  <TeamFormationDisplay
                    teamType='away'
                    formationData={activePlayers.awayTeam.formation}
                    mode={mode}
                    fieldWidth={fieldWidth}
                    borderColor={getTeamColor(config?.dataAwayTeamColor, footballSingleEvent.awayTeam.shirtColor, false)}
                    handleClick={handleClick}
                  />
                </FieldContainer>
              )}
              {config?.dataHeaderDisplay && mode !== GenericLayout.HORIZONTAL && teamHeads && (
                <MobileHeaderFooter small={fieldWidth < THUMB_SIZE_BREAKPOINT_3}>{teamHeads[1]}</MobileHeaderFooter>
              )}
            </FieldWrapper>
            {details[LineupsDetails.COACH].show && lineups.homeTeam.coach && lineups.awayTeam.coach && (
              <CoachContainer
                mode={mode === GenericLayout.HORIZONTAL ? GenericLayout.HORIZONTAL : GenericLayout.VERTICAL}
                homeTeam={{
                  coach: lineups.homeTeam.coach,
                  formation: lineups.homeTeam.formation,
                  borderColor: getTeamColor(config?.dataHomeTeamColor, footballSingleEvent.homeTeam.shirtColor),
                }}
                awayTeam={{
                  coach: lineups.awayTeam.coach,
                  formation: lineups.awayTeam.formation,
                  borderColor: getTeamColor(config?.dataAwayTeamColor, footballSingleEvent.awayTeam.shirtColor, false),
                }}
                formationLabel={config?.dataLabels?.formation ?? 'Formations'}
                handleClick={handleClick}
              />
            )}
            {mode !== GenericLayout.HORIZONTAL && details.showTabs && (
              <MobileTabs>
                <MobileTab onClick={() => handleTabClickAndSendInteractionEvent(0)} selected={selectedTabMobile === 0}>
                  {footballSingleEvent.homeTeam.name}
                </MobileTab>
                <MobileTab onClick={() => handleTabClickAndSendInteractionEvent(1)} selected={selectedTabMobile === 1}>
                  {footballSingleEvent.awayTeam.name}
                </MobileTab>
              </MobileTabs>
            )}
            <CollapsibleContainer>
              {details[LineupsDetails.SUBSTITUTES].show && (
                <LineupCollapsible
                  title={config?.dataLabels?.substituted_players ?? 'Substituted Players'}
                  homeTeamPlayers={activePlayers.homeTeam.substituedPlayers}
                  awayTeamPlayers={activePlayers.awayTeam.substituedPlayers}
                  footballSingleEvent={footballSingleEvent}
                  handleClick={handleClick}
                  selectedTabMobile={selectedTabMobile}
                  mode={mode}
                  initialState='open'
                  verticalMode={mode !== GenericLayout.HORIZONTAL}
                />
              )}
              {details[LineupsDetails.FIRST_TEAM].show && (
                <LineupCollapsible
                  title={config?.dataLabels?.first_team ?? 'Starting lineups'}
                  homeTeamPlayers={activePlayers.homeTeam.first}
                  awayTeamPlayers={activePlayers.awayTeam.first}
                  footballSingleEvent={footballSingleEvent}
                  handleClick={handleClick}
                  selectedTabMobile={selectedTabMobile}
                  mode={mode}
                  initialState={details[LineupsDetails.FIRST_TEAM].status}
                  verticalMode={mode !== GenericLayout.HORIZONTAL}
                />
              )}
              {details[LineupsDetails.SUBSTITUTES].show && (
                <LineupCollapsible
                  title={config?.dataLabels?.substitutes ?? 'Substitutes'}
                  homeTeamPlayers={activePlayers.homeTeam.notSubstitutedPlayers}
                  awayTeamPlayers={activePlayers.awayTeam.notSubstitutedPlayers}
                  footballSingleEvent={footballSingleEvent}
                  handleClick={handleClick}
                  selectedTabMobile={selectedTabMobile}
                  mode={mode}
                  initialState={details[LineupsDetails.SUBSTITUTES].status}
                  verticalMode={mode !== GenericLayout.HORIZONTAL}
                />
              )}
            </CollapsibleContainer>
          </LinkServiceContext.Provider>
        )}
      </ThemeProvider>
    </ConfigContext.Provider>
  );
};

const DesktopHeader = styled.withConfig({ componentId: 'LineupsWidget', displayName: 'DesktopHeader' })('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: '$lineupsHeaderContainerBgColor',
  height: '64px',
  maxWidth: '100%',
  margin: '0 auto',
  padding: '12px 8px',
  borderTopLeftRadius: '8px',
  borderTopRightRadius: '8px',

  [`& > ${SMPLinkWrapper} > div`]: {
    margin: 0,
    width: '50%',
  },

  variants: {
    wide: {
      true: {
        width: '772px',
      },
      false: {
        width: '938px',
        borderBottomLeftRadius: '8px',
        borderBottomRightRadius: '8px',
        marginBottom: '20px',
      },
    },
  },
});

export const MAX_LINEUPS_WIDTH = 938;

const FieldWrapper = styled.withConfig({ componentId: 'LineupsWidget', displayName: 'FieldWrapper' })('div', {
  color: '$lineupsHeaderContainerBgColor',
  maxWidth: '772px',
  margin: '0 auto',
});

const CollapsibleContainer = styled.withConfig({ componentId: 'LineupsWidget', displayName: 'CollapsibleContainer' })('div', {
  display: 'flex',
  flexDirection: 'column',
  margin: '0 auto',
  maxWidth: `${MAX_LINEUPS_WIDTH}px`,
  gap: '20px',
});

const MobileHeaderFooter = styled.withConfig({ componentId: 'LineupsWidget', displayName: 'MobileHeaderFooter' })('div', {
  backgroundColor: '$lineupsHeaderContainerBgColor',
  paddingLeft: '$3',
  maxWidth: '393px',
  margin: '0 auto',
  variants: {
    small: {
      true: { py: '$2' },
      false: {},
    },
  },
});

const FieldContainer = styled.withConfig({ componentId: 'LineupsWidget', displayName: 'FieldContainer' })('div', {
  display: 'flex',
  position: 'relative',
  // width: '100%', // Width is now handled by variants
  color: '$footballFieldLineColor', // Moved from inline css
  backgroundColor: '$footballFieldTone', // Moved from inline css
  margin: '0 auto',
  backgroundRepeat: 'no-repeat',
  backgroundSize: '100%',

  '& > div': {
    position: 'absolute',
  },

  variants: {
    mode: {
      [GenericLayout.HORIZONTAL]: {
        // Use enum value
        width: '772px',
        height: '418px',
        maxWidth: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        borderBottomLeftRadius: '8px',
        borderBottomRightRadius: '8px',
      },
      [GenericLayout.VERTICAL]: {
        // Add variant
        maxWidth: '393px',
        width: '100%',
        height: '635px',
        alignItems: 'center',
        justifyContent: 'center',
      },
      [GenericLayout.VERTICAL_CONDENSED]: {
        // Add variant
        maxWidth: '393px',
        width: '100%',
        height: '635px',
        alignItems: 'center',
        justifyContent: 'center',
      },
    },
    noHeader: {
      true: {
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
      },
    },
  }, // variants object ends here
}); // styled call ends here

// Re-add MobileTabs and MobileTab
const MobileTabs = styled.withConfig({ componentId: 'LineupsWidget', displayName: 'MobileTabs' })('div', {
  display: 'grid',
  columnGap: '$2',
  gridTemplateColumns: '1fr 1fr',
});

const MobileTab = styled.withConfig({ componentId: 'LineupsWidget', displayName: 'MobileTab' })('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '$3',
  color: '$hiContrast',
  fontFamily: '$primaryFontFamily',
  textAlign: 'center',
  cursor: 'pointer',
  fontWeight: '700',

  variants: {
    selected: {
      true: {
        backgroundColor: '$collapsibleBgColor',
        borderTopRightRadius: '$3',
        borderTopLeftRadius: '$3',
      },
      false: {
        backgroundColor: 'transparent',
        opacity: 0.7,
        color: '$lineupsSecondaryTextColor',
        fontWeight: '600',
      },
    },
  },
});
